import {DestroyRef, Directive, effect, ElementRef, inject, input} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, fromEvent} from 'rxjs';
import {AutoTabIndexTrapDirective} from './auto-tab-index-trap.directive';

// מגדיר כיצד יש להתנהג כאשר מגיעים עם טאב לאלמנט
// לדוגמה בכפתור: נלחץ עליו
type AutoTabIndexBehavior = 'tab' | 'click';

@Directive({
	selector: '[autoTabIndex]',
})
export class AutoTabIndexDirective {
	public readonly el = inject(ElementRef);
	public readonly trap = inject(AutoTabIndexTrapDirective);
	public readonly autoTabIndex = input('tab', {transform: (value: string) => (value || 'tab') as AutoTabIndexBehavior});
	private readonly destroyRef = inject(DestroyRef);
	private assignedTabIndex: number = NaN;

	constructor() {
		this.setTabIndex();
		const behaviourEffect = effect(() => {
			const behaviour = this.autoTabIndex();
			this.initTabIndexBehavior(behaviour);
			behaviourEffect.destroy();
		});
	}

	private setTabIndex(): void {
		const el = this.el.nativeElement;
		this.assignedTabIndex = this.trap.getTabIndex();
		el.tabIndex = this.assignedTabIndex;
	}

	private initTabIndexBehavior(behaviour: AutoTabIndexBehavior): void {
		// fromEvent(this.el.nativeElement, 'keydown')
		// 	.pipe(
		// 		filter(event => event instanceof KeyboardEvent && event.key === 'Tab'),
		// 		takeUntilDestroyed(this.destroyRef),
		// 	)
		// 	.subscribe((event: KeyboardEvent) => {
		// 		switch (behaviour) {
		// 			case 'click':
		// 				// if the behavior is click, tab act as a click
		// 				this.likeClickHandler(event);
		// 				break;
		// 			case 'tab':
		// 				// if the behavior is tab, focus the element
		// 				this.el.nativeElement.focus();
		// 				break;
		// 		}
		// 	});

		switch (behaviour) {
			case 'click':
				// we need to listen to focus event of the element
				fromEvent(this.el.nativeElement, 'focus')
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((focusEvent: FocusEvent) => {
						this.likeClickHandler(focusEvent);
					});
				break;
			case 'tab':
				// we need to listen to keydown event of the element
				fromEvent(this.el.nativeElement, 'keydown')
					.pipe(
						filter(event => event instanceof KeyboardEvent && event.key === 'Tab'),
						takeUntilDestroyed(this.destroyRef),
					)
					.subscribe((event: KeyboardEvent) => {
						// במקרה של לחיצה על Tab, נרצה לעבור לאלמנט הבא
						event.preventDefault();
						this.el.nativeElement.focus();
						this.jumpToNextElement();
					});
				break;
		}
	}

	private likeClickHandler(event: KeyboardEvent | FocusEvent): void {
		const el = this.el.nativeElement;
		el.click();
		// after click we want to jump to the next element
		this.jumpToNextElement();
	}

	private jumpToNextElement(): void {
		// find the next element with auto-tab-index directive
		const nextIndex = this.assignedTabIndex + 1;
		// חיפוש אלמנט הבא - תומך גם ב-autoTabIndex="tab" וגם ב-autotabindex ללא ערך
		const selector = `[autotabindex][tabIndex="${nextIndex}"]`;
		const nextElement = document.querySelector(selector);
		if (nextElement && nextElement instanceof HTMLElement) {
			setTimeout(() => {
				nextElement.focus();
			}, 0);
		} else {
			// return to the first element in the trap
			this.trap.restartTrapCycle();
		}
	}
}
